/**
 * API client for the corporate-partnerships backend.
 *
 * In dev, Vite proxies /api -> http://localhost:8001 (see vite.config.ts).
 * In production the SPA is served from the same origin as the API, so a
 * relative /api path works everywhere — no base URL juggling required.
 */

export type SponsorshipType =
  | 'fully_sponsored'
  | 'discounted'
  | 'co_funded';

export type QrPlacement = 'in_store' | 'online' | 'both' | 'none';

/** Mirrors the backend ApplicationCreate Pydantic model (snake_case JSON). */
export interface ApplicationPayload {
  company_name: string;
  company_reg_no?: string;
  industry: string;
  website: string;
  contact_name: string;
  email: string;
  phone: string;
  prize_product: string;
  prize_value: string;
  sponsorship_type: SponsorshipType;
  campaign_objective: string;
  target_audience: string;
  preferred_dates: string;
  qr_placement: QrPlacement;
  wants_leads: boolean;
  wants_ai_marketing: boolean;
  image_urls?: string[];
  notes?: string;
  /** POPIA — the consent box; the backend records it with a server timestamp. */
  consent_acknowledged: boolean;
  /** Honeypot — must be empty. Bots fill it; humans never see it. */
  company_url?: string;
}

export interface ApplicationResponse {
  id: string;
  reference: string;
  status: string;
  created_at: string;
}

// --- Campaign dashboard (epic-6) -------------------------------------------

/** Winner verification + prize-delivery status (mirrors backend FunnelWinner). */
export interface FunnelWinner {
  verified: boolean;
  delivered: boolean;
  delivery_video_url: string | null;
}

/** Traffic-source split (mirrors backend FunnelTrafficSources). */
export interface FunnelTrafficSources {
  qr: number;
  link: number;
  direct: number;
}

/**
 * Combined per-campaign funnel + leads roll-up.
 * Mirrors the backend CampaignFunnelResponse (GET .../funnel).
 */
export interface CampaignFunnel {
  campaign_id: string;
  competition_id: string | null;
  status: string;
  /** Canonical {event_type: count}; missing events normalised to 0. */
  funnel: Record<string, number>;
  conversion_rate: number | null;
  traffic_sources: FunnelTrafficSources;
  winner: FunnelWinner;
  /** Consented-lead COUNT only — never any per-lead PII. */
  leads: number;
  as_of: string | null;
  /** Data older than the freshness window (real but old). */
  stale: boolean;
  /** Funnel is last-known / zeroed (prizes unavailable), not a live roll-up. */
  degraded: boolean;
}

/**
 * Fetch the combined funnel roll-up for one campaign.
 *
 * Behind the fail-closed internal-staff auth stub: 501 in prod until v1-2-6
 * (partner-scoped auth) lands. A 200 may still carry degraded/stale data — the
 * dashboard renders those states rather than treating them as errors.
 */
export async function fetchCampaignFunnel(
  campaignId: string,
): Promise<CampaignFunnel> {
  let resp: Response;
  try {
    resp = await fetch(
      `/api/v1/admin/campaigns/${encodeURIComponent(campaignId)}/funnel`,
    );
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }

  if (!resp.ok) {
    const message =
      resp.status === 501
        ? 'Partner dashboards are not yet available in this environment.'
        : resp.status === 404
          ? 'We could not find that campaign.'
          : 'We could not load this campaign right now. Please try again.';
    throw new ApiError(message, resp.status);
  }

  return (await resp.json()) as CampaignFunnel;
}

/** URL of the downloadable CSV report for a campaign (auth-gated, same stub). */
export function campaignReportUrl(campaignId: string): string {
  return `/api/v1/admin/campaigns/${encodeURIComponent(campaignId)}/report`;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public detail?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

// ============================================================================
// Self-serve campaign builder (epic-10, v1-10-2)
// ============================================================================
//
// These hit /api/v1/partner/campaigns — the partner-facing front door to the
// epic-2 review state machine. The backend gates the surface behind the
// internal-staff auth stub (fail-closed in prod until real partner auth lands,
// v1-2-6), so in prod these calls return 501 until then. The UI surfaces that
// gracefully.

/** Skill challenges are decided by SKILL — never a draw / lottery / chance. */
export interface SkillChallengeOption {
  value: string;
  label: string;
}

export interface SelfServeOptions {
  skill_challenge_types: SkillChallengeOption[];
}

/** Mirrors the backend SelfServeDraftCreate model (snake_case JSON). */
export interface DraftCreatePayload {
  partner_id: string;
  name: string;
  prize_title?: string;
  prize_description?: string;
  prize_estimated_value_cents?: number;
  prize_currency?: string;
  skill_challenge_type?: string;
  brand_assets_url?: string;
  tracking_notes?: string;
  fulfilment_note?: string;
  starts_at?: string;
  ends_at?: string;
  leads_capture_enabled?: boolean;
  ai_optin_enabled?: boolean;
}

export type DraftUpdatePayload = Partial<Omit<DraftCreatePayload, 'partner_id'>>;

/** Mirrors the backend CampaignRead model (subset the wizard needs). */
export interface CampaignRead {
  id: string;
  partner_id: string;
  status: string;
  name: string;
  prize_title?: string | null;
  prize_description?: string | null;
  prize_estimated_value_cents?: number | null;
  prize_currency?: string | null;
  cash_equivalent_allowed: boolean;
  skill_challenge_type?: string | null;
  brand_assets_url?: string | null;
  tracking_notes?: string | null;
  fulfilment_note?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  leads_capture_enabled: boolean;
  lead_consent_recorded: boolean;
  ai_optin_enabled: boolean;
  // Prizes link refs (set on create/link). public_url is the friendly
  // /win/<slug> competition URL shareable with the partner.
  competition_id?: string | null;
  tracking_url?: string | null;
  qr_png_url?: string | null;
  public_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompliancePreview {
  ready_to_submit: boolean;
  violations: string[];
}

async function parseError(resp: Response, fallback: string): Promise<ApiError> {
  let detail: unknown;
  try {
    detail = await resp.json();
  } catch {
    detail = undefined;
  }
  let message = fallback;
  if (resp.status === 501) {
    message =
      'The self-serve campaign builder is not yet available in this environment. ' +
      'Please contact our team to set up your campaign.';
  } else if (resp.status === 422) {
    message = 'Some details need attention — please review the highlighted fields.';
  }
  return new ApiError(message, resp.status, detail);
}

async function jsonFetch<T>(
  input: string,
  init: RequestInit,
  fallback: string,
): Promise<T> {
  // These callers are all partner-scoped (/partner/campaigns/*). Attach the
  // partner JWT (read the key directly — importing partnerAuth here would create
  // a cycle: partnerAuth already imports from this module).
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  try {
    const token = sessionStorage.getItem('cp_partner_token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    /* storage unavailable */
  }
  let resp: Response;
  try {
    resp = await fetch(input, { ...init, headers });
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }
  if (!resp.ok) throw await parseError(resp, fallback);
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export function getBuilderOptions(): Promise<SelfServeOptions> {
  return jsonFetch<SelfServeOptions>(
    '/api/v1/partner/campaigns/options',
    { method: 'GET' },
    'Could not load campaign builder options.',
  );
}

export function createDraft(payload: DraftCreatePayload): Promise<CampaignRead> {
  return jsonFetch<CampaignRead>(
    '/api/v1/partner/campaigns',
    { method: 'POST', body: JSON.stringify(payload) },
    'Could not save your campaign draft.',
  );
}

export function updateDraft(
  id: string,
  payload: DraftUpdatePayload,
): Promise<CampaignRead> {
  return jsonFetch<CampaignRead>(
    `/api/v1/partner/campaigns/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Could not save your changes.',
  );
}

export function previewDraft(id: string): Promise<CompliancePreview> {
  return jsonFetch<CompliancePreview>(
    `/api/v1/partner/campaigns/${id}/preview`,
    { method: 'GET' },
    'Could not check your campaign.',
  );
}

export function submitDraft(id: string): Promise<CampaignRead> {
  return jsonFetch<CampaignRead>(
    `/api/v1/partner/campaigns/${id}/submit`,
    { method: 'POST' },
    'Could not submit your campaign for review.',
  );
}

export async function submitApplication(
  payload: ApplicationPayload,
): Promise<ApplicationResponse> {
  let resp: Response;
  try {
    resp = await fetch('/api/v1/applications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }

  if (!resp.ok) {
    let detail: unknown;
    try {
      detail = await resp.json();
    } catch {
      detail = undefined;
    }
    const message =
      resp.status === 422
        ? 'Some details need attention — please review the highlighted fields.'
        : 'Something went wrong submitting your application. Please try again.';
    throw new ApiError(message, resp.status, detail);
  }

  return (await resp.json()) as ApplicationResponse;
}
